import React, { useState, useEffect } from "react";
import "./TradeStatusIndicator.css";

interface TradeStatusIndicatorProps {
  tradeId: number;
  isClosed: boolean;
  onReopen?: () => void;
  className?: string;
}

const TradeStatusIndicator: React.FC<TradeStatusIndicatorProps> = ({
  tradeId,
  isClosed,
  onReopen,
  className = "",
}) => {
  const [isReopening, setIsReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);

  const handleReopen = async () => {
    if (!onReopen) return;

    setIsReopening(true);
    setReopenError(null);

    try {
      await onReopen();
    } catch (error) {
      setReopenError(
        error instanceof Error ? error.message : "Failed to reopen trade",
      );
    } finally {
      setIsReopening(false);
    }
  };

  return (
    <div className={`trade-status-indicator ${className}`}>
      <div className={`status-badge ${isClosed ? "closed" : "open"}`}>
        <span className="status-icon">{isClosed ? "🔒" : "🔓"}</span>
        <span className="status-text">
          {isClosed ? "Trade Closed" : "Trade Open"}
        </span>
      </div>

      {isClosed && (
        <div className="status-actions">
          <div className="status-message">
            This trade is closed and cannot be modified.
            {onReopen && " You can reopen it to make changes."}
          </div>

          {onReopen && (
            <button
              onClick={handleReopen}
              disabled={isReopening}
              className="reopen-button"
            >
              {isReopening ? "Reopening..." : "Reopen Trade"}
            </button>
          )}

          {reopenError && (
            <div className="reopen-error">Error: {reopenError}</div>
          )}
        </div>
      )}

      {!isClosed && (
        <div className="status-message open">
          This trade is open and can be modified.
        </div>
      )}
    </div>
  );
};

export default TradeStatusIndicator;
